import { hardReload, purgeAppShell, SHELL_PURGE_KEY } from './shell-recovery';

/**
 * Detects a failed lazy-chunk / dynamic-import load. These are NOT application
 * bugs — they happen when a client running an old build requests a hashed chunk
 * that a newer deploy has already removed from the origin (a stale-version 404),
 * or during the brief window before the service worker controls the page. They
 * are self-healing: a reload onto the fresh, no-store `index.html` (and the
 * up-to-date service-worker version) resolves them. So they must be recovered
 * from with a reload, never shown as the fatal "Something went wrong" fallback.
 *
 * Covers the wording used by Angular/webpack, Vite/esbuild, Chromium, Firefox,
 * and Safari for the same underlying failure.
 */
const CHUNK_LOAD_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [^\s]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

/** True when `message` looks like a lazy-chunk / dynamic-import load failure. */
export function isChunkLoadError(message: string): boolean {
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/** sessionStorage: timestamp of the last automatic chunk-error reload. */
export const CHUNK_RELOAD_KEY = 'jiopc-chunk-reload-at';
/** sessionStorage: set after the automatic service-worker purge for a chunk failure. */
export const CHUNK_PURGE_KEY = 'jiopc-chunk-purged';

/**
 * Clears automatic chunk-recovery markers so a user-initiated hard reload can
 * run a fresh reload→purge sequence instead of immediately re-showing the
 * fatal fallback (the soft-reload loop after "Reload Page").
 */
export function clearChunkRecoveryMarkers(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    sessionStorage.removeItem(CHUNK_PURGE_KEY);
  } catch {
    // sessionStorage unavailable — recovery still proceeds.
  }
}

/**
 * How long to wait before allowing another chunk-error reload. If a reload
 * doesn't resolve the chunk failure (e.g. the origin is genuinely broken), a
 * second reload within this window is suppressed so we fall through to the
 * error fallback instead of reloading forever.
 */
export const CHUNK_RELOAD_WINDOW_MS = 20_000;

/**
 * Decide whether to auto-reload on a chunk-load error: yes on the first
 * occurrence, and again only after the guard window has elapsed since the last
 * attempt — so a persistent failure can't loop.
 */
export function shouldReloadForChunkError(
  nowMs: number,
  lastAttemptMs: number | null,
  windowMs: number = CHUNK_RELOAD_WINDOW_MS,
): boolean {
  if (lastAttemptMs === null) return true;
  return nowMs - lastAttemptMs >= windowMs;
}

/** What to do with a lazy-chunk failure. Never the fatal screen until both recoveries have been tried. */
export type ChunkRecovery = 'reload' | 'purge' | 'fallback';

/**
 * First failure reloads onto the current build. A second failure inside the
 * guard window means that reload did not help — the service worker is still
 * pinned to a manifest that does not contain the chunk (typical right after
 * "Update now"). Drop the worker and its caches once, then load from the
 * network. Only a failure after that purge is a real broken deploy.
 */
export function chunkRecoveryAction(
  nowMs: number,
  lastAttemptMs: number | null,
  alreadyPurged: boolean,
  windowMs: number = CHUNK_RELOAD_WINDOW_MS,
): ChunkRecovery {
  if (shouldReloadForChunkError(nowMs, lastAttemptMs, windowMs)) return 'reload';
  if (!alreadyPurged) return 'purge';
  return 'fallback';
}

/**
 * Runs the reload/purge recovery for a lazy-chunk failure, wherever it
 * surfaces: Angular's global `ErrorHandler` (a chunk failure inside already-
 * running code) or a Router `NavigationError` (a chunk failure during
 * navigation — including the very first navigation after a reload, which the
 * Router does not rethrow into the zone/global `ErrorHandler`, so without this
 * it leaves `<router-outlet>` with nothing activated: a blank screen instead of
 * the fatal fallback. See app.ts's `trackRouteLoading` for that callsite.
 *
 * Returns true once a reload/purge is in flight (navigation about to happen);
 * false if `error` is not a chunk-load error, or both recoveries were already
 * tried this session — the caller should fall through to its own fallback.
 */
export async function recoverFromChunkLoadError(error: unknown): Promise<boolean> {
  const message = error instanceof Error ? error.message : String(error ?? '');
  if (!isChunkLoadError(message)) return false;

  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    const last = raw && !Number.isNaN(Number(raw)) ? Number(raw) : null;
    const alreadyPurged = sessionStorage.getItem(CHUNK_PURGE_KEY) === '1';
    const action = chunkRecoveryAction(Date.now(), last, alreadyPurged);

    if (action === 'reload') {
      sessionStorage.removeItem(CHUNK_PURGE_KEY);
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
      window.location.reload();
      return true;
    }
    if (action === 'purge') {
      // The reload landed on a service worker still pinned to a manifest that
      // does not contain this chunk (typical right after "Update now", or a
      // chunk request that was served by a different backend replica mid-deploy
      // than the one that served index.html). Drop the worker and its caches
      // once, then load from the network instead of the crash screen.
      sessionStorage.setItem(CHUNK_PURGE_KEY, '1');
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
      const now = Date.now();
      try {
        // Share the boot-script guard so the reloaded document does not purge
        // again inside the same window if it also fails to start.
        sessionStorage.setItem(SHELL_PURGE_KEY, String(now));
      } catch {
        // sessionStorage unavailable — still try to load a clean document.
      }
      try {
        await purgeAppShell();
      } catch {
        // A network navigation is still better than staying on the broken shell.
      }
      hardReload(now);
      return true;
    }
  } catch {
    // sessionStorage/window unavailable — fall through to the caller's fallback.
  }
  return false;
}
